import React, { useState } from 'react';
import { Grid, TextField, Button, useTheme, Typography, Alert, Box, Avatar } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { completeSocialSignup, clearPendingSocialSignup } from 'app/shared/reducers/authentication';
import LoaderIcon from 'app/components/common/loader-icon/loader-icon';
import PhoneInput, { isValidPhoneNumber, parsePhoneNumber, type Country } from 'react-phone-number-input';
import { detectCountryFromTimezone } from 'app/utils/common-utils/common-utils';
// @ts-ignore — CSS import from react-phone-number-input has no type declaration
import 'react-phone-number-input/style.css';

interface SocialSignupCompletionProps {
  setCurrentComponent?: any;
  handleAuthModalClose?: any;
}

// Shown after a brand-new Google/Apple sign-in (POST socialLogin returned PROFILE_INCOMPLETE).
// Collects date of birth + a verified phone number, then applies the same India age rule
// email/password signup uses before the account is actually created.
const SocialSignupCompletion = ({ setCurrentComponent, handleAuthModalClose }: SocialSignupCompletionProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const pendingSocialSignup = useAppSelector(state => state.authentication.pendingSocialSignup);
  const loading = useAppSelector(state => state.authentication.loading);
  const errorMessage = useAppSelector(state => state.authentication.errorMessage);

  const [dateOfBirth, setDateOfBirth] = useState('');
  const [parentEmail, setParentEmail] = useState('');
  const [ageTier, setAgeTier] = useState<'BLOCKED' | 'CONSENT_REQUIRED' | 'NORMAL'>('NORMAL');
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState('');

  const [defaultCountry] = useState<Country>(() => (detectCountryFromTimezone() as Country) ?? 'IN');
  const [phoneValue, setPhoneValue] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [otpError, setOtpError] = useState('');
  const [countryCode, setCountryCode] = useState('');
  const [isoCode, setIsoCode] = useState('');
  const [verifiedPhoneNumber, setVerifiedPhoneNumber] = useState('');

  const getAgeTier = (dob: string, code: string): 'BLOCKED' | 'CONSENT_REQUIRED' | 'NORMAL' => {
    if (!dob || code !== '+91') return 'NORMAL';
    const birthDate = new Date(dob);
    const today = new Date();
    const age =
      today.getFullYear() -
      birthDate.getFullYear() -
      (today < new Date(today.getFullYear(), birthDate.getMonth(), birthDate.getDate()) ? 1 : 0);
    if (age < 16) return 'BLOCKED';
    if (age < 18) return 'CONSENT_REQUIRED';
    return 'NORMAL';
  };

  const handleSendOtp = async () => {
    if (!phoneValue) return;
    setPhoneError('');
    try {
      const phoneInfo = parsePhoneNumber(phoneValue);
      if (!phoneInfo || !isValidPhoneNumber(phoneValue)) {
        setPhoneError('Please enter a valid phone number.');
        return;
      }
      const code = `+${phoneInfo.countryCallingCode}`;
      setOtpSending(true);
      await axios.post('api/phone/sendOtp', { phoneNumber: phoneInfo.nationalNumber, countryCode: code, medium: 'sms' });
      setOtpSent(true);
    } catch {
      setPhoneError('Failed to send OTP. Please try again.');
    } finally {
      setOtpSending(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otpCode || otpCode.length < 4) return;
    setOtpError('');
    const phoneInfo = parsePhoneNumber(phoneValue);
    if (!phoneInfo) {
      setOtpError('Invalid phone number.');
      return;
    }
    const code = `+${phoneInfo.countryCallingCode}`;
    const nationalNumber = phoneInfo.nationalNumber;
    const iso = phoneInfo.country || '';
    try {
      setOtpVerifying(true);
      const res = await axios.post('api/phone/verify-pre-registration', {
        phoneNumber: nationalNumber,
        countryCode: code,
        isoCode: iso,
        code: otpCode,
      });
      if (res.data?.verified) {
        setPhoneVerified(true);
        setCountryCode(code);
        setIsoCode(iso);
        setVerifiedPhoneNumber(nationalNumber);
        const tier = getAgeTier(dateOfBirth, code);
        setAgeTier(tier);
        if (tier !== 'CONSENT_REQUIRED') setParentEmail('');
      } else {
        setOtpError('Incorrect OTP. Please try again.');
      }
    } catch {
      setOtpError('Incorrect OTP. Please try again.');
    } finally {
      setOtpVerifying(false);
    }
  };

  const handleBack = () => {
    dispatch(clearPendingSocialSignup());
    setCurrentComponent('login');
  };

  const isBlocked = ageTier === 'BLOCKED';
  const isMinor = ageTier === 'CONSENT_REQUIRED';
  const isSubmitDisabled = submitting || loading || !phoneVerified || !dateOfBirth || isBlocked || (isMinor && !parentEmail);

  const handleSubmit = async () => {
    if (!pendingSocialSignup) return;
    setLocalError('');
    setSubmitting(true);
    try {
      const result = await dispatch(
        completeSocialSignup({
          pendingSignupToken: pendingSocialSignup.pendingSignupToken,
          dateOfBirth,
          phoneNumber: verifiedPhoneNumber,
          countryCode,
          isoCode,
          parentEmail: isMinor ? parentEmail : undefined,
        })
      );
      if ((result as any)?.payload && !(result as any)?.error) {
        toast.success('Welcome to ReachAMY!');
        handleAuthModalClose?.();
        navigate('/welcome');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!pendingSocialSignup) {
    return null;
  }

  return (
    <Grid container spacing={2} direction={'column'} justifyContent={'center'} alignItems={'center'} px={8} py={3} sx={{ margin: 'auto' }}>
      <Box
        sx={{
          border: 'none',
          maxHeight: '80vh',
          maxWidth: '460px',
          textAlign: 'center',
          overflowY: 'scroll',
          overflowX: 'hidden',
          '&::-webkit-scrollbar': { display: 'none' },
          '&::-webkit-scrollbar-thumb': { backgroundColor: 'secondary.contrastText', height: '10px' },
          '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
        }}
      >
        <Box display="flex" justifyContent="center" mb={1}>
          <Avatar src={pendingSocialSignup.imageUrl} alt={pendingSocialSignup.firstName} />
        </Box>
        <Typography sx={{ fontSize: '15px', fontWeight: 600, mb: 1 }}>
          Welcome, {pendingSocialSignup.firstName}! Just a couple more details to finish setting up your account.
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          {pendingSocialSignup.email}
        </Typography>

        <Grid container spacing={1}>
          {/* Phone number with OTP */}
          <Grid item xs={12} md={12} className="pt-1">
            <Box sx={{ textAlign: 'left' }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                Phone number
              </Typography>
              <style>{`input.PhoneInputInput { border-radius: 8px !important; border: 1px solid #bfbfbf !important; padding: 8px; font-size: 14px; }`}</style>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Box sx={{ flex: 1 }}>
                  <PhoneInput
                    placeholder="Enter phone number"
                    value={phoneValue}
                    defaultCountry={defaultCountry}
                    onChange={val => {
                      setPhoneValue(val || '');
                      if (phoneVerified) {
                        setPhoneVerified(false);
                        setCountryCode('');
                        setIsoCode('');
                        setVerifiedPhoneNumber('');
                        setAgeTier('NORMAL');
                      }
                      setOtpSent(false);
                      setPhoneError('');
                      setOtpCode('');
                      setOtpError('');
                    }}
                    limitMaxLength
                    countryCallingCodeEditable={false}
                    international
                  />
                </Box>
                {phoneVerified ? (
                  <Typography variant="caption" color="success.main" sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                    ✓ Verified
                  </Typography>
                ) : (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => void handleSendOtp()}
                    disabled={!phoneValue || otpSending}
                    sx={{ whiteSpace: 'nowrap', borderRadius: '8px', textTransform: 'none', fontSize: '12px' }}
                  >
                    {otpSending ? <LoaderIcon /> : otpSent ? 'Resend' : 'Send OTP'}
                  </Button>
                )}
              </Box>
              {phoneError && <Typography variant="caption" color="error">{phoneError}</Typography>}
            </Box>
          </Grid>
          {otpSent && !phoneVerified && (
            <Grid item xs={12} md={12}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
                <TextField
                  size="small"
                  placeholder="Enter 6-digit OTP"
                  value={otpCode}
                  onChange={e => { setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setOtpError(''); }}
                  inputProps={{ maxLength: 6, style: { letterSpacing: '4px', fontWeight: 600 } }}
                  sx={{ flex: 1 }}
                />
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => void handleVerifyOtp()}
                  disabled={otpCode.length < 4 || otpVerifying}
                  sx={{
                    whiteSpace: 'nowrap',
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontSize: '12px',
                    backgroundColor: theme.palette.primary.light,
                    color: 'white',
                    boxShadow: 'none',
                    '&:hover': { backgroundColor: theme.palette.primary.light, boxShadow: 'none' },
                  }}
                >
                  {otpVerifying ? <LoaderIcon /> : 'Verify'}
                </Button>
              </Box>
              {otpError && <Typography variant="caption" color="error">{otpError}</Typography>}
            </Grid>
          )}
          {/* DOB */}
          <Grid item xs={12} md={12} className="pt-3">
            <TextField
              size="small" fullWidth name="dateOfBirth" type="date" InputLabelProps={{ shrink: true }} label="Date of Birth"
              value={dateOfBirth}
              onChange={e => {
                setDateOfBirth(e.target.value);
                const tier = getAgeTier(e.target.value, countryCode);
                setAgeTier(tier);
                if (tier !== 'CONSENT_REQUIRED') setParentEmail('');
              }}
              inputProps={{ max: new Date().toISOString().split('T')[0] }}
            />
          </Grid>
          {isBlocked && (
            <Grid item xs={12} md={12}>
              <Alert severity="error" sx={{ maxWidth: '100%' }}>
                <Typography variant="subtitle1" fontSize={'10px'}>
                  You must be at least 16 years old to create an account.
                </Typography>
              </Alert>
            </Grid>
          )}
          {isMinor && (
            <Grid item xs={12} md={12} className="pt-3">
              <TextField
                size="small" fullWidth name="parentEmail" type="email" label="Parent / Guardian Email"
                helperText="Required for Indian users aged 16 and 17 (DPDP Act)"
                value={parentEmail}
                onChange={e => setParentEmail(e.target.value)}
              />
            </Grid>
          )}
          {(localError || errorMessage) && (
            <Grid item xs={12} md={12}>
              <Alert severity="error" sx={{ maxWidth: '100%' }}>
                <Typography variant="subtitle1" fontSize={'10px'}>{localError || errorMessage}</Typography>
              </Alert>
            </Grid>
          )}
          <Grid item xs={12} md={12} className="text-center pt-2">
            <Button
              variant="contained" color="primary" onClick={() => void handleSubmit()} disabled={isSubmitDisabled}
              sx={{
                marginBottom: '10px',
                height: theme.createActivity.height,
                backgroundColor: theme.palette.primary.light,
                borderRadius: theme.createActivity.borderRadius,
                color: theme.palette.secondary.dark,
                borderColor: theme.palette.primary.main,
                boxShadow: 'none',
                '&:active, &:focus, &:hover': { backgroundColor: theme.createActivity.backgroundColor.hover, boxShadow: 'none' },
              }}
              endIcon={submitting ? <LoaderIcon /> : null}
            >
              Finish creating account
            </Button>
            {!phoneVerified && (
              <Typography variant="caption" color="text.secondary" display="block">
                Verify your phone number to continue
              </Typography>
            )}
          </Grid>
          <Grid item xs={12} md={12} pt={0}>
            <Typography
              sx={{ fontSize: '11px', color: theme.palette.primary.light, fontWeight: 600, cursor: 'pointer' }}
              onClick={handleBack}
            >
              Back
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </Grid>
  );
};

export default SocialSignupCompletion;
